import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { AdminPage, AdminState, isForbidden } from '@/admin/AdminShell'
import { StateView } from '@/card/record'
import { formatMoment } from '@/card/recordHelpers'
import { fetchClientRisk, type ClientRiskView, type LevelFilter } from './clientRiskApi'

/**
 * Клиенты в зоне риска (#135).
 *
 * - **Список считает сервер**, в области `sales.counterparty:read` смотрящего: менеджер видит
 *   своих клиентов, руководитель — отдела или всех. Экран ничего не досчитывает.
 * - **Правило, а не модель**: веса заданы рассуждением, и экран говорит это вслух — иначе балл
 *   читается как вероятность ухода, которой никто не мерил.
 * - **Причины — словами сервера**: они собраны из фактов (сколько писем без ответа, какие жалобы,
 *   какой счёт к сроку), и пересказ на клиенте разошёлся бы с ними при первой правке.
 */
export function ClientRiskPage() {
  const { t } = useApp()
  const [level, setLevel] = useState<LevelFilter>('all')
  const list = useQuery({
    queryKey: ['client-risk', level],
    queryFn: ({ signal }) => fetchClientRisk(level, signal),
    retry: false,
  })
  const clients = list.data?.clients ?? []
  const rule = list.data?.rule

  return (
    <AdminPage sectionKey="nav.section.sales" titleKey="nav.clientRisk" count={list.data ? clients.length : undefined}
               forbidden={isForbidden(list.error)}>
      {rule && (
        <p className="page-lead">
          {t('clientRisk.intro', {
            high: rule.high, medium: rule.medium, silence: rule.silenceDays, touches: rule.minUnanswered,
            complaints: rule.complaintDays, due: rule.dueSoonDays,
          })}
        </p>
      )}
      <div className="row" role="group" aria-label={t('clientRisk.filter')} style={{ gap: 8, marginBottom: 12 }}>
        {(['all', 'high', 'medium'] as const).map((value) => (
          <button key={value} type="button" aria-pressed={level === value}
                  className={level === value ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
                  onClick={() => setLevel(value)}>
            {t(`clientRisk.filter.${value}`)}
          </button>
        ))}
      </div>
      <AdminState isLoading={list.isLoading} error={list.error} onRetry={() => void list.refetch()}>
        {clients.length === 0 ? (
          <StateView boxed kind="empty" title={t('clientRisk.empty')} description={t('clientRisk.emptyText')} />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl" aria-label={t('nav.clientRisk')}>
              <thead>
                <tr>
                  <th>{t('clientRisk.col.client')}</th>
                  <th>{t('clientRisk.col.level')}</th>
                  <th className="num">{t('clientRisk.col.score')}</th>
                  <th>{t('clientRisk.col.reasons')}</th>
                  <th>{t('clientRisk.col.responsible')}</th>
                  <th>{t('clientRisk.col.since')}</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => <RiskRow key={client.counterpartyId} client={client} />)}
              </tbody>
            </table>
          </div>
        )}
        {list.data?.truncated && <p className="muted t-sm">{t('clientRisk.truncated')}</p>}
      </AdminState>
    </AdminPage>
  )
}

function RiskRow({ client }: { client: ClientRiskView }) {
  const { t, locale } = useApp()
  return (
    <tr data-client={client.counterpartyId}>
      <td>
        <Link to={`/counterparties/${client.counterpartyId}`}>{client.counterpartyName}</Link>
        {client.inn != null && <div className="muted t-sm">{client.inn}</div>}
      </td>
      <td><LevelBadge level={client.level} /></td>
      <td className="num">{client.score}</td>
      <td>
        <ul className="stack" style={{ gap: 4, margin: 0, paddingLeft: 16 }}>
          {(client.reasons ?? []).map((reason) => <li key={reason.code}>{reason.text}</li>)}
        </ul>
      </td>
      <td>
        {client.responsibleName ?? <span className="muted">{t('clientRisk.noResponsible')}</span>}
        {client.level === 'high' && client.announced && !client.anyoneTold && (
          <div className="t-sm danger">{t('clientRisk.nobodyTold')}</div>
        )}
      </td>
      <td>{client.riskSince == null ? '—' : formatMoment(client.riskSince, locale)}</td>
    </tr>
  )
}

export function LevelBadge({ level }: { level: ClientRiskView['level'] }) {
  const { t } = useApp()
  const tone = level === 'high' ? 'b-danger' : level === 'medium' ? 'b-warning' : ''
  return <span className={`badge ${tone}`}>{t(`clientRisk.level.${level}`)}</span>
}
